/**
 * 알림 시드 — 사람마다 쌓인 앱 안 알림. 눌렀을 때 갈 화면은 `link` 에 들어 있다.
 *
 * 종류를 보고 화면을 짜 맞추지 않는다 — 서버가 넣어 준 링크로 간다.
 */
import type { Notification } from "@/types";
import { at } from "./seed";

/** 지금으로부터 몇 분 전 — 패널의 오늘·어제·이번 주 묶음이 언제 열어도 채워지게 시각을 지금에 맞춘다. */
const ago = (minutes: number) => new Date(Date.now() - minutes * 60_000).toISOString();
const HOUR = 60;
const DAY = 24 * HOUR;

export interface MockNotification extends Notification {
  user_id: string;
}

export const notifications: MockNotification[] = [
  {
    id: "notif-1",
    user_id: "u-staff",
    actor: { id: "u-pm", name: "노은진", avatar_url: null },
    kind: "daily_comment",
    title: "Daily 코멘트: 노은진",
    body: "실시설계 상세도 쪽 시간이 좀 많아 보여요. 어떤 작업이었는지 한 줄 더 적어 주세요.",
    link: "/daily",
    read_at: null,
    created_at: at("2026-10-21", "19:12"),
  },
  {
    id: "notif-2",
    user_id: "u-staff",
    actor: null,
    kind: "vacation_decided",
    title: "휴가 자동 승인",
    body: "2026-10-16 16:00–18:00 · 0.25일",
    link: "/attendance",
    read_at: at("2026-10-07", "10:30"),
    created_at: at("2026-10-07", "10:12"),
  },
  {
    id: "notif-3",
    user_id: "변경민",
    actor: { id: "u-st3", name: "오세진", avatar_url: null },
    kind: "vacation_requested",
    title: "휴가 신청: 오세진",
    body: "2026-10-23 · 1일 · 개인 연차",
    link: "/admin/attendance",
    read_at: null,
    created_at: at("2026-10-19", "09:40"),
  },
  {
    id: "notif-4",
    user_id: "변경민",
    actor: null,
    kind: "report_ready",
    title: "주간 리포트가 만들어졌어요",
    body: "2026-10-12 ~ 2026-10-18",
    link: "/reports",
    read_at: null,
    created_at: at("2026-10-19", "00:05"),
  },

  // 김태호(관리자) — 결재 요청이 쌓이는 쪽
  {
    id: "notif-5",
    user_id: "u-owner",
    actor: { id: "u-st2", name: "김서연", avatar_url: null },
    kind: "vacation_requested",
    title: "휴가 신청: 김서연",
    body: "2026-11-03 · 1일 · 개인 연차",
    link: "/attendance",
    read_at: null,
    created_at: ago(10),
  },
  {
    id: "notif-6",
    user_id: "u-owner",
    actor: { id: "u-st4", name: "최다민", avatar_url: null },
    kind: "daily_edit_requested",
    title: "Daily 수정 요청: 최다민",
    body: "2026-10-24 기록 · 프로젝트 시간을 고쳐 달라고 했어요",
    link: "/daily",
    read_at: null,
    created_at: ago(40),
  },
  {
    id: "notif-7",
    user_id: "u-owner",
    actor: { id: "u-pm2", name: "송지하", avatar_url: null },
    kind: "vacation_cancel_requested",
    title: "휴가 취소 신청: 송지하",
    body: "2026-10-30 · 0.5일 · 오전 반차",
    link: "/attendance",
    read_at: null,
    created_at: ago(3 * HOUR),
  },
  {
    id: "notif-8",
    user_id: "u-owner",
    actor: { id: "u-pm", name: "노은진", avatar_url: null },
    kind: "report_comment",
    title: "리포트 코멘트: 노은진",
    body: "성수동 대수선 다음 주 진행 예정을 적어 뒀어요.",
    link: "/reports",
    read_at: ago(20 * HOUR),
    created_at: ago(DAY + 2 * HOUR),
  },
  {
    id: "notif-9",
    user_id: "u-owner",
    actor: null,
    kind: "report_ready",
    title: "주간 리포트가 만들어졌어요",
    body: "2026-10-19 ~ 2026-10-25",
    link: "/reports",
    read_at: ago(DAY),
    created_at: ago(DAY + 9 * HOUR),
  },
  {
    id: "notif-10",
    user_id: "u-owner",
    actor: null,
    kind: "assignment_changed",
    title: "투입 인원 변경: 성수동 대수선",
    body: "오세진 님이 11월 1일부터 함께해요",
    link: "/projects",
    read_at: ago(2 * DAY),
    created_at: ago(3 * DAY),
  },
  {
    id: "notif-11",
    user_id: "u-owner",
    actor: { id: "변경민", name: "변경민", avatar_url: null },
    kind: "vacation_decided",
    title: "휴가 승인: 김태호",
    body: "2026-10-10 · 1일 · 개인 연차",
    link: "/attendance",
    read_at: ago(9 * DAY),
    created_at: ago(10 * DAY),
  },
  // 이도윤(직원) — 내 신청의 결과와 배치
  {
    id: "notif-12",
    user_id: "u-staff",
    actor: null,
    kind: "assignment_changed",
    title: "투입 인원 변경: 성수동 대수선",
    body: "11월 1일부터 이 프로젝트에 들어가요",
    link: "/projects",
    read_at: null,
    created_at: ago(2 * HOUR),
  },
  {
    id: "notif-13",
    user_id: "u-staff",
    actor: { id: "u-owner", name: "김태호", avatar_url: null },
    kind: "daily_edit_decided",
    title: "Daily 수정 요청이 승인됐어요",
    body: "2026-10-20 기록",
    link: "/daily",
    read_at: ago(DAY),
    created_at: ago(DAY + 4 * HOUR),
  },
  {
    id: "notif-14",
    user_id: "u-staff",
    actor: null,
    kind: "daily_missing",
    title: "Daily를 아직 안 썼어요",
    body: "2026-10-24(금)",
    link: "/daily",
    read_at: null,
    created_at: ago(3 * DAY),
  },
];
